import React, { useEffect, useState } from 'react';
import { Button, Grid, Skeleton, styled, Typography, useTheme } from '@mui/material';
import CustomAutocomplete, { ItemProps } from './CustomAutocomplete';
import ProfileMenu, { ProfileMenuProps } from './ProfileMenu';
import { fixedDigitsFormat } from '../../utils/functions';
import { DefaultSelectedItem } from './MobileMenu';
import Submenu, { SubmenuProps } from './SubMenus';
import useGetCurrency from '../../hooks/useGetCurrency';

export interface HorizontalMenuProps {
  submenus: SubmenuProps[];
  defaultSelectedMenu: DefaultSelectedItem;
  companies: ItemProps[];
  activeCompany: string;
  balance: number;
  profileMenu: ProfileMenuProps;
  setItem: (value: ItemProps | null) => void;
  isUserAdmin: boolean;
  isLoadingBalance: boolean;
  isLoadingCompanies: boolean;
  icon?: React.JSX.Element;
  returnToHomeFn?: () => void;
}

const HorizontalMenu: React.FC<HorizontalMenuProps> = ({
  submenus,
  defaultSelectedMenu,
  companies,
  activeCompany,
  isLoadingCompanies,
  balance,
  profileMenu,
  setItem,
  isLoadingBalance,
  isUserAdmin,
  icon,
  returnToHomeFn,
}) => {
  const [selectedMenu, setSelectedMenu] = useState<DefaultSelectedItem>(defaultSelectedMenu);
  const theme = useTheme();

  if (!isUserAdmin) {
    // filter out admin only content
    submenus = submenus.filter((submenu) => !submenu.isAdminOnlyContent);
  }
  useEffect(() => {
    if (defaultSelectedMenu !== selectedMenu) setSelectedMenu(defaultSelectedMenu);
  }, [defaultSelectedMenu]);

  return (
    <WrapperGrid container style={{ alignItems: 'center', margin: '0 auto' }}>
      <Grid item lg={1} xl={1.5}>
        {icon && (
          <Button
            disableRipple
            sx={{
              p: 0,
              width: 100,
              '&:hover': { backgroundColor: 'transparent' },
            }}
            variant="text"
            onClick={returnToHomeFn}
            data-testid="icon-img"
          >
            {icon}
          </Button>
        )}
      </Grid>
      <Grid item lg={7} xl={6.5}>
        {submenus.map((submenu) => (
          <Submenu {...submenu} key={submenu.buttonTitle} setSelectedMenu={setSelectedMenu} selectedMenu={selectedMenu} />
        ))}
      </Grid>
      <Grid item md={4} style={{ display: 'flex', alignItems: 'center' }}>
        <Grid item md={6} style={{ display: 'flex', justifyContent: 'center' }}>
          {isUserAdmin && companies.length > 0 && (
            <CustomAutocomplete items={companies} activeItem={activeCompany} setItem={setItem} textFieldTitle="Active Company" isLoading={isLoadingCompanies} />
          )}
        </Grid>

        <Grid
          item
          md={6}
          style={{
            display: 'flex',
            alignItems: 'center',
            float: 'right',
            justifyContent: 'end',
            gap: theme.spacing(1),
          }}
        >
          <div
            style={{
              marginRight: theme.spacing(1.5),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 600, marginRight: 4 }}>
              Balance:
            </Typography>
            {isLoadingBalance ? (
              <Skeleton width={110} />
            ) : (
              <Typography variant="body2" style={{ textWrap: 'nowrap' }}>{`${useGetCurrency()} ${fixedDigitsFormat(balance, 2)}`}</Typography>
            )}
          </div>
          <ProfileMenu {...profileMenu} />
        </Grid>
      </Grid>
    </WrapperGrid>
  );
};

export default HorizontalMenu;

const WrapperGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(1),
  maxWidth: 1920,
  paddingBottom: theme.spacing(4),
}));
